import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'components/atoms';
import TrackFormCard from 'components/molecules/card/track-form/TrackFormCard';
import ViewHint from 'components/molecules/hint/ViewHint';
import TrackSelectPopupForm from 'components/molecules/popup/track-select/TrackSelectPopupForm';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiFolderPlus, PiWaveform } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { IMusicProductDigitalAlbum } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { measuring } from 'utils/dnd/dndUtilities';

type ParentType = 'MUSIC_PRODUCT_DIGITAL';

interface ITracksStepTabProps {
  propertyName?: 'tracks';
  parent: ParentType;
}

const NAVIGATION_MAP = {
  MUSIC_PRODUCT_DIGITAL: {
    storeKey: 'musicProductDigitalForm' as const,
    basePath: 'music-product-digital',
    next: 'data',
    back: undefined,
    hint: 'music-product-digital-tracks',
  },
};

const TracksStepTab = <
  Form extends Record<'tracks', IMusicProductDigitalAlbum[]> & Record<'id', string>,
>({
  parent,
}: ITracksStepTabProps) => {
  const navigate = useNavigate();
  const { setForm } = useStoreState();
  const { t } = useTranslation();
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();

  const config = NAVIGATION_MAP[parent];

  const sortableItems = useMemo(
    () =>
      (values.tracks[0]?.tracks ?? []).map((track, index) => ({
        ...track,
        dndId: `${track.id}-${index}`,
        originalIndex: index,
      })),
    [values.tracks],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = sortableItems.find((item) => item.dndId === active.id)?.originalIndex;
      const newIndex = sortableItems.find((item) => item.dndId === over.id)?.originalIndex;

      if (oldIndex !== undefined && newIndex !== undefined) {
        const movedArray = arrayMove(values.tracks[0]?.tracks, oldIndex, newIndex);
        setFieldValue('tracks[0].tracks', movedArray);
      }
    }
    document.body.style.cursor = '';
  };

  const handleNavigate = (direction: 'next' | 'back') => {
    if (!config) return;

    setForm(config.storeKey, values as any);

    const subPath = config[direction];
    const action = values.id ? `edit/${values.id}` : 'create';

    navigate(`/${config.basePath}/${action}/${subPath}`);
  };

  return (
    <StepsTab
      isFirst
      nextName={config?.next}
      // nextDisabled={() => !values.tracks?.length}
      onNext={() => handleNavigate('next')}
    >
      <ViewHint hintId={config.hint} />

      {!status?.displayMode && (
        <div className="flex flex-col gap-4 w-full sm:flex-row">
          <Button
            variant={BUTTON_VARIANT.white}
            size="large"
            fullWidth
            onClick={() => {
              setForm(config.storeKey, values as any);
              navigate(`/track/create/file`);
            }}
          >
            <Button.Icon icon={<PiWaveform />} />
            <Button.Text text={t('button.add-new-track')} />
          </Button>
          <TrackSelectPopupForm
            onSelectTrack={(track, actions) => {
              setFieldValue(`tracks[0].tracks[${sortableItems.length}]`, {
                name: track.mainTitle.title,
                id: track.id,
                isrc: track.isrc,
                version: track.mainTitle.version,
                trackStatus: track.trackStatus,
                wavLink: track.wavLink,
                mp3Link: track.mp3Link,
                flacLink: track.flacLink,
                mp3PreviewLink: track.mp3PreviewLink,
              });
              actions.close();
            }}
            trigger={
              <Button variant={BUTTON_VARIANT.white} size="large" fullWidth>
                <Button.Icon icon={<PiFolderPlus />} />
                <Button.Text text={t('button.select-track-from-catalog')} />
              </Button>
            }
            disabledTracks={values.tracks[0]?.tracks.map((t) => t.id)}
          />
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={() => (document.body.style.cursor = 'grabbing')}
        onDragEnd={handleDragEnd}
        onDragCancel={() => (document.body.style.cursor = '')}
      >
        <SortableContext
          items={sortableItems?.map((i) => i.dndId)}
          strategy={verticalListSortingStrategy}
        >
          {sortableItems?.map((track, i) => (
            <TrackFormCard
              key={track.dndId}
              index={i}
              track={track}
              sortableKey={track.dndId}
              onGoEdit={() => {
                setForm(config.storeKey, values as any);
                navigate(`/track/edit/${track.id}/file`);
              }}
              onRemove={() =>
                setFieldValue(
                  'tracks[0].tracks',
                  values.tracks[0]?.tracks.filter((t) => t.id !== track.id),
                )
              }
              displayMode={status?.displayMode}
            />
          ))}
        </SortableContext>
      </DndContext>
    </StepsTab>
  );
};

export default TracksStepTab;
